/**
 * Coordinates MLP chat triggers when multiple are armed:
 * - `enqueueFirstWinsChatTrigger` — if several triggers flush in the same turn,
 *   only the first scheduled run executes (FIFO / first wins).
 * - Pending timeouts are keyed by `triggerKey` (latest schedule per key wins).
 *   When any trigger actually runs, `cancelAllPendingScheduledTriggers` clears
 *   every other pending countdown so only one modal / chat open path wins.
 */

import { scheduleChatTriggerDelay } from "@/hooks/chat/helpers/chatTriggerDelay";

const pendingByTriggerKey = new Map();

/**
 * @param {string} triggerKey
 * @param {string} label
 * @param {number} seconds
 * @param {() => void} onComplete
 * @returns {() => void} cancel function
 */
export const scheduleTrackedChatTriggerDelay = (
  triggerKey,
  label,
  seconds,
  onComplete,
) => {
  const key = triggerKey ?? "__anon__";
  const previous = pendingByTriggerKey.get(key);
  if (previous) previous();

  let cancelTracked = () => {};

  const cancelTimeout = scheduleChatTriggerDelay(label, seconds, () => {
    if (pendingByTriggerKey.get(key) === cancelTracked) {
      pendingByTriggerKey.delete(key);
    }
    onComplete();
  });

  cancelTracked = () => {
    if (pendingByTriggerKey.get(key) === cancelTracked) {
      pendingByTriggerKey.delete(key);
    }
    cancelTimeout();
  };

  pendingByTriggerKey.set(key, cancelTracked);
  return cancelTracked;
};

export const CHAT_PENDING_TRIGGERS_CLEARED = "chat-pending-triggers-cleared";

export const cancelAllPendingScheduledTriggers = () => {
  const cancels = Array.from(pendingByTriggerKey.values());
  const hadPending = cancels.length > 0;
  pendingByTriggerKey.clear();
  cancels.forEach((cancel) => {
    try {
      cancel();
    } catch {
      /* noop */
    }
  });
  if (hadPending && typeof document !== "undefined") {
    document.dispatchEvent(new CustomEvent(CHAT_PENDING_TRIGGERS_CLEARED));
  }
};

let firstWinsMicrotaskScheduled = false;
let firstWinsFn = null;

/**
 * Runs the first `fn` queued before the microtask; later calls in the same turn
 * are ignored (first wins, not last).
 */
export const enqueueFirstWinsChatTrigger = (fn) => {
  if (firstWinsMicrotaskScheduled) return;
  firstWinsMicrotaskScheduled = true;
  firstWinsFn = fn;
  queueMicrotask(() => {
    const run = firstWinsFn;
    firstWinsMicrotaskScheduled = false;
    firstWinsFn = null;
    run?.();
  });
};

export const CHAT_PROACTIVE_SESSION_RESET = "chat-proactive-session-reset";

export const dispatchChatProactiveSessionReset = () => {
  if (typeof document === "undefined") return;
  document.dispatchEvent(new CustomEvent(CHAT_PROACTIVE_SESSION_RESET));
};

/** While true, proactive modal `onClose` skips session reset (close-then-reopen swap). */
let suppressProactiveModalCloseSessionReset = false;

export const runWithSuppressedProactiveModalCloseSessionReset = (fn) => {
  suppressProactiveModalCloseSessionReset = true;
  try {
    fn();
  } finally {
    suppressProactiveModalCloseSessionReset = false;
  }
};

/**
 * After proactive chat `<dialog>` closes. Dispatches `CHAT_PROACTIVE_SESSION_RESET`
 * (bumps `sessionEpoch`, cancels pending timers) but does not re-arm proactive
 * triggers — `proactiveSessionConsumed` / `proactiveV2TriggersConsumed` stay set
 * until `chatConfig` changes.
 */
export const handleProactiveMicroModalDidClose = () => {
  if (suppressProactiveModalCloseSessionReset) return;
  dispatchChatProactiveSessionReset();
};
